"use client";

import React from "react";
import RelatedTopics from "@/components/tools/RelatedTopics";
import Hero from "@/components/tools/Hero";
import GoToEmergencyDentist from "@/components/services/emergency-treatment/GoToEmergencyDentist";
import CommonEmergencies from "@/components/services/emergency-treatment/CommonEmergencies";
import PatientExperiences from "@/components/services/emergency-treatment/PatientExperiences";
import WhyChooseSmile4Me from "@/components/services/emergency-treatment/WhyChooseSmile4Me";
import WalkInNow from "@/components/services/emergency-treatment/WalkInNow";
import ServiceAuthorBio from "@/components/services/ServiceAuthorBio";
import FAQ from "@/components/tools/FAQ";

const emergencyFaqs = [
  {
    question: "How fast can I be seen for a dental emergency in Astoria?",
    answer:
      "Most emergencies are seen the same day you call. Dr. Iosif Khaimov and the Smile4Me team keep room in the schedule specifically for urgent cases, so call (718) 400-4400 as soon as symptoms start rather than waiting to see if the pain passes.",
  },
  {
    question: "Do I need to be an existing patient to walk in?",
    answer:
      "No. Smile4Me welcomes new patients for emergency visits, so you don't need any prior appointment history with our office to be seen. Just call ahead so the team can prepare for your visit and get you in as quickly as possible.",
  },
  {
    question: "What should I do while I wait to get to the office?",
    answer:
      "For a toothache, rinse with warm salt water and take an over-the-counter pain reliever as directed. For a knocked-out tooth, keep it moist in milk and avoid touching the root. For swelling or bleeding, apply a cold compress or gentle pressure with a clean cloth on the way in.",
  },
  {
    question: "When should I go to the emergency room instead of the dentist?",
    answer:
      "Go to the ER or call 911 if you have severe facial swelling, difficulty breathing or swallowing, uncontrolled bleeding, or a head, jaw, or facial injury. For everything else, from a broken tooth to an abscess, an emergency dentist is the faster and more appropriate first call.",
  },
  {
    question: "Do you offer weekend or evening emergency appointments?",
    answer:
      "Smile4Me sees emergency patients Sunday from 11:00 am to 5:00 pm in addition to weekday hours through 7:00 pm. Call as soon as your emergency comes up, even outside typical business hours, and our team will do everything possible to get you seen quickly.",
  },
  {
    question:
      "How do you handle payment for emergency treatment if I don't have insurance?",
    answer:
      "Most PPO insurance plans are accepted, but insurance isn't required to be seen. Smile4Me offers financing with up to two years, 0% interest, so cost doesn't have to delay urgent care.",
  },
];

const EmergencyTreatment: React.FC = () => {
  return (
    <>
      <Hero
        imageSrc="/images/photo-patient-receiving-oral-exam-from-emergency-dentist-astoria-with-dental-mirror-and-tools.webp"
        imageAlt="photo-patient-receiving-oral-exam-from-emergency-dentist-astoria-with-dental-mirror-and-tools"
        title="Emergency Dentist in Astoria, NY"
        subtitle="Same-day relief for tooth pain, trauma, and infections at Smile4Me Dental Care."
        ctaText="Call (718) 400-4400"
        ctaLink="tel:+17184004400"
      />
      <section
        className="border-y border-[#ECD05B]/40 bg-[#faf8f2] px-4 py-4"
        aria-label="Smile4Me office hours"
      >
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-center gap-2 text-center text-sm text-gray-800 sm:flex-row sm:gap-6 sm:text-base">
          <p><strong>Mon–Thu:</strong> 10:00 am–7:00 pm</p>
          <p><strong>Friday:</strong> 10:00 am–2:00 pm</p>
          <p><strong>Saturday:</strong> Closed</p>
          <p><strong>Sunday:</strong> 11:00 am–5:00 pm</p>
          <a
            href="tel:+17184004400"
            className="inline-block bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text font-semibold text-transparent hover:underline transition-all duration-300 ease-in-out"
          >
            Call now: (718) 400-4400
          </a>
        </div>
      </section>
      <main className="flex-1 py-8">
        <div className="flex flex-col lg:flex-row mx-auto px-4 sm:px-8 md:px-12">
          <article className="flex-1 space-y-8 pr-0 lg:pr-8">
            <div className="w-full py-6 sm:py-8 bg-gradient-to-b from-white to-gray-50">
              <div className="max-w-6xl mx-auto px-4 sm:px-8 md:px-12 space-y-6 text-center md:text-left">
                <p className="text-gray-700 text-base sm:text-lg leading-relaxed group hover:bg-gray-50 transition-all duration-300 rounded-md py-1">
                  Whether you&apos;re dealing with a severe toothache at
                  midnight, a knocked-out tooth after an accident, or a broken
                  crown before an important event, the team at Smile4Me Dental
                  Care is here to help.
                </p>
                <p className="text-gray-700 text-base sm:text-lg leading-relaxed group hover:bg-gray-50 transition-all duration-300 rounded-md py-1">
                  As an emergency dentist in Astoria, Queens, we&apos;re here to
                  see you immediately. Call us now at{" "}
                  <a
                    href="tel:+17184004400"
                    className="inline-block bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent hover:underline transition-all duration-300 ease-in-out"
                  >
                    (718) 400-4400
                  </a>
                  .
                </p>
              </div>
            </div>
            <CommonEmergencies />
            <GoToEmergencyDentist />
            <ServiceAuthorBio />
            <PatientExperiences />
            <WhyChooseSmile4Me />
            <WalkInNow />
            <FAQ title="Frequently Asked Questions" faqs={emergencyFaqs} />
          </article>
          <RelatedTopics
            topics={[
              { label: "Extractions", url: "/services/extractions" },
              { label: "Root Canals", url: "/services/root-canals" },
              { label: "Sedation Dentistry", url: "/services/sedation-dentistry" },
              { label: "Dental Fillings", url: "/services/dental-fillings" },
              {
                label: "CEREC Same-Day Crowns",
                url: "/services/cerec-single-visit-crowns",
              },
              { label: "Oral Exams", url: "/services/oral-exams" },
            ]}
          />
        </div>
      </main>
    </>
  );
};

export default EmergencyTreatment;
